import { useEffect, useRef, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useI18n } from '../i18n';
import { SUPPORTED_LOCALES, LOCALE_LABELS, LOCALE_SHORT, type Locale } from '../i18n/locales';
import { useAuth } from '../state/AuthContext';
import { apiPost } from '../lib/api';

// The menu never scrolled and never flipped, so it was sized for the THREE
// locales this file's comment still described. At ten it is ~400px tall, and the
// switcher's main home is the BOTTOM of the desktop rail — so on a 14-inch screen
// the list opened downward straight off the viewport and the last languages
// (Punjabi, Assamese, Tamil…) could not be reached at all. Night 16 filed it, and
// the shape of the bug is worth keeping: the component was correct when written
// and was silently invalidated by Conv-28/31 ADDING DATA, with no gate able to
// see it because nothing about "3 items" was ever written down.
const MENU_MAX_PX = 320;
// w-40. Needed as a number because the horizontal anchor has to be decided the
// same way the vertical one is — see `alignLeft` below.
const MENU_WIDTH_PX = 160;
// Breathing room between the trigger and the viewport edge, on whichever side the
// menu opens. Matches the mt-1.5/mb-1.5 offset plus a little.
const MENU_GAP_PX = 8;
// Never shrink below two rows plus the scrollbar: a 20px-tall scroller is not a
// menu, and at that point showing a short scrollable list beats showing a sliver.
const MENU_MIN_PX = 96;

// Language toggle — ten locales; see SUPPORTED_LOCALES, never a hand-typed list
// here (a count in a comment is exactly what went stale above). Switching
// re-renders every
// consumer instantly (in-memory override). For a signed-in user it also
// persists to the account (so the choice follows them to other devices); a
// signed-out visitor gets the on-device override only. Failures are silent —
// the on-device switch already applied, so the UI is never left wrong.
export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  const { session, csrfToken, refreshSession } = useAuth();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  // The menu was anchored `right-0` unconditionally. In the desktop rail the
  // trigger sits ~75px from the left edge, so a 160px menu ran from x=-85: it
  // rendered OFF the left of the viewport and every language name was clipped
  // away, leaving a blank panel painted over the nav labels. Fixing the vertical
  // overflow is what made this visible — the menu was previously off-screen
  // downward as well, so the horizontal clipping never got a chance to show.
  // Two overflow bugs on one element, and only the second one was filed.
  const [alignLeft, setAlignLeft] = useState(false);
  // Conv-41: the height the menu is ALLOWED to be, given the side it opened on.
  // A fixed cap plus a flip is not enough — see `toggle`.
  const [menuMax, setMenuMax] = useState(MENU_MAX_PX);
  const ref = useRef<HTMLDivElement | null>(null);

  // Measured at OPEN time, not at mount: the rail switcher and the header
  // switcher are the same component in very different places, and a resize or a
  // scroll between renders would strand a stale decision.
  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - MENU_GAP_PX;
      const above = r.top - MENU_GAP_PX;
      // THE FLIP MUST COMPARE THE TWO SIDES, NOT TEST ONE OF THEM. The first
      // version asked only "is there room below?" and flipped up whenever the
      // answer was no — including when there was even LESS room above, which is
      // the normal case for the header switcher on a short window. The menu then
      // rendered off the TOP of the viewport and not one language was reachable:
      // the same defect as the original, mirrored, and reintroduced by its own fix.
      const up = below < Math.min(MENU_MAX_PX, above);
      setDropUp(up);
      // And clamping is the other half. Choosing the roomier side still overflows
      // when NEITHER side can hold the list — ten locales at ~400px on a short
      // window — so the menu is capped to the room the chosen side actually has
      // and scrolls inside it. `overflow-y-auto` was already there; without this
      // it never had a height small enough to need it.
      setMenuMax(Math.max(MENU_MIN_PX, Math.min(MENU_MAX_PX, up ? above : below)));
      setAlignLeft(r.right - MENU_WIDTH_PX < 8);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = async (l: Locale) => {
    setLocale(l);
    setOpen(false);
    if (session && csrfToken) {
      try {
        await apiPost('/api/account/preferences', { locale: l, csrfToken });
        await refreshSession();
      } catch {
        /* on-device switch already applied; account sync is best-effort */
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-tour="language"
        className={compact ? 'btn-ghost rounded-lg p-2' : 'btn-ghost flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs'}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Language')}
      >
        <Globe size={compact ? 18 : 15} />
        {compact ? null : <span className="font-semibold">{LOCALE_SHORT[locale]}</span>}
      </button>
      {open ? (
        <div
          role="menu"
          style={{ maxHeight: menuMax }}
          className={`absolute z-50 w-40 overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-lg dark:border-line-dark dark:bg-surface-dark ${
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          } ${alignLeft ? 'left-0' : 'right-0'}`}
        >
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              role="menuitemradio"
              aria-checked={l === locale}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-sunken dark:hover:bg-surface-dark-sunken"
              onClick={() => void pick(l)}
            >
              <span>{LOCALE_LABELS[l]}</span>
              {l === locale ? <Check size={14} className="text-brand-700 dark:text-brand-300" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
